import React, { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Loader2, MessageSquareQuote, RotateCcw, Save, Sparkles } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import adminService from '@services/adminService';
import type { AIAgent, AIAgentForm, AIBrandVoice } from '@/types/admin';
import { AI_PURPOSE_LABELS } from '@/types/admin';
import { formatDateTimeBR } from '@/utils/formatters';

const intOrNull = (v: string): number | null => (v.trim() === '' ? null : Number.parseInt(v, 10));
const floatOrNull = (v: string): number | null => (v.trim() === '' ? null : Number(v.replace(',', '.')));

/** Um agente: prompt (padrão do sistema ou editado) e parâmetros. */
const AgentCard: React.FC<{ agent: AIAgent; onSaved: (a: AIAgent) => void; onError: (e: unknown) => void }> = ({ agent, onSaved, onError }) => {
  const [prompt, setPrompt] = useState(agent.system_prompt ?? agent.default_prompt);
  const [effort, setEffort] = useState(agent.effort ?? '');
  const [maxTokens, setMaxTokens] = useState(agent.max_tokens?.toString() ?? '');
  const [temperature, setTemperature] = useState(agent.temperature?.toString() ?? '');
  const [variations, setVariations] = useState(agent.variations?.toString() ?? '');
  const [frames, setFrames] = useState(agent.max_frames?.toString() ?? '');
  const [enabled, setEnabled] = useState(agent.is_enabled);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const customized = agent.system_prompt !== null && agent.system_prompt.trim() !== '';
  const isEmbed = agent.agent_key === 'embed';
  const isText = agent.purpose === 'copywriting';

  const save = async (resetPrompt = false) => {
    setSaving(true);
    try {
      const form: AIAgentForm = {
        is_enabled: enabled,
        effort,
        max_tokens: intOrNull(maxTokens) ?? undefined,
        temperature: floatOrNull(temperature) ?? undefined,
        variations: intOrNull(variations) ?? undefined,
        max_frames: intOrNull(frames) ?? undefined,
      };
      if (!isEmbed) {
        // Igual ao padrão = não grava cópia (o padrão evolui com o sistema).
        form.system_prompt = resetPrompt || prompt.trim() === agent.default_prompt.trim() ? '' : prompt;
      }
      const saved = await adminService.saveAgent(agent.agent_key, form);
      if (resetPrompt) setPrompt(saved.default_prompt);
      onSaved(saved);
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-h4 flex items-center gap-2">
            <Sparkles size={16} className="text-emerald-600" /> {agent.name}
            {!agent.is_enabled && <span className="badge-danger">Desligado</span>}
            {customized && <span className="badge-warning">Prompt editado</span>}
          </h2>
          <p className="text-small text-slate-500 mt-1">
            {agent.description} · usa o provedor de <strong>{AI_PURPOSE_LABELS[agent.purpose]}</strong>
          </p>
          {agent.updated_at && (
            <p className="text-[11px] text-slate-400">
              Alterado em {formatDateTimeBR(agent.updated_at)} por {agent.updated_by}
            </p>
          )}
        </div>
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="checkbox-field" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Ligado
        </label>
      </div>

      {!isEmbed && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <label className="block">
            <span className="text-label">Esforço</span>
            <select className="select-field mt-1" value={effort} onChange={(e) => setEffort(e.target.value)}>
              <option value="">Padrão</option>
              <option value="low">Baixo</option>
              <option value="medium">Médio</option>
              <option value="high">Alto</option>
            </select>
          </label>
          <label className="block">
            <span className="text-label">Máx. tokens</span>
            <input className="input-field mt-1" inputMode="numeric" value={maxTokens} onChange={(e) => setMaxTokens(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-label">Temperatura (0–1)</span>
            <input className="input-field mt-1" inputMode="decimal" value={temperature} placeholder="padrão" onChange={(e) => setTemperature(e.target.value)} />
          </label>
          {isText && (
            <label className="block">
              <span className="text-label">Variações</span>
              <input className="input-field mt-1" inputMode="numeric" value={variations} onChange={(e) => setVariations(e.target.value)} />
            </label>
          )}
          {agent.agent_key === 'describe_media' && (
            <label className="block">
              <span className="text-label">Quadros por vídeo (1–6)</span>
              <input className="input-field mt-1" inputMode="numeric" value={frames} onChange={(e) => setFrames(e.target.value)} />
            </label>
          )}
        </div>
      )}

      {!isEmbed && (
        <div>
          <button type="button" className="text-small text-emerald-700 hover:underline" onClick={() => setOpen((o) => !o)}>
            {open ? 'Ocultar prompt' : 'Ver / editar prompt'}
          </button>
          {open && (
            <textarea
              className="textarea-field mt-2 font-mono text-xs"
              rows={16}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" icon={<Save />} loading={saving} onClick={() => save(false)}>
          Salvar
        </Button>
        {customized && (
          <Button size="sm" variant="outline" icon={<RotateCcw />} onClick={() => save(true)} disabled={saving}>
            Voltar ao prompt padrão
          </Button>
        )}
      </div>
    </section>
  );
};

const VOICE_FIELDS: { key: keyof AIBrandVoice; label: string; rows: number; help: string }[] = [
  { key: 'tone', label: 'Tom', rows: 2, help: 'Como a marca fala (ex.: sofisticado, acolhedor, prático).' },
  { key: 'guidelines', label: 'Diretrizes', rows: 4, help: 'O que valorizar, estrutura das legendas, chamadas para ação.' },
  { key: 'forbidden_words', label: 'Palavras proibidas', rows: 2, help: 'Separadas por ponto e vírgula.' },
  { key: 'emoji_policy', label: 'Emojis', rows: 1, help: 'Quantos e quais emojis usar.' },
  { key: 'examples', label: 'Exemplos de legendas aprovadas', rows: 6, help: 'Cole 2 ou 3 legendas que representam bem a marca.' },
];

/** Admin → IA → Agentes: prompts, parâmetros e tom de voz da marca. */
export const AIAgentsView: React.FC = () => {
  const [agents, setAgents] = useState<AIAgent[]>([]);
  const [voice, setVoice] = useState<AIBrandVoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingVoice, setSavingVoice] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, v] = await Promise.all([adminService.listAgents(), adminService.listBrandVoices()]);
      setAgents(a);
      setVoice(v.find((x) => x.is_default) ?? v[0] ?? null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveVoice = async () => {
    if (!voice) return;
    setSavingVoice(true);
    try {
      setVoice(await adminService.saveBrandVoice({ ...voice, is_default: true, is_active: true }, voice.id || undefined));
    } catch (err) {
      setError(err);
    } finally {
      setSavingVoice(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h3 flex items-center gap-2">
          <Sparkles size={20} className="text-slate-500" /> IA — Agentes e tom de voz
        </h1>
        <p className="text-small text-slate-500 mt-1">
          Cada agente é uma tarefa da IA no Marketing. O prompt padrão vem com o sistema; editar cria uma versão própria
          que deixa de receber as melhorias do padrão até você voltar a ele.
        </p>
      </div>

      {voice && (
        <section className="card p-4 space-y-3">
          <h2 className="text-h4 flex items-center gap-2">
            <MessageSquareQuote size={16} className="text-emerald-600" /> Tom de voz da marca
          </h2>
          <p className="text-small text-slate-500">Enviado aos agentes de texto (legendas, hashtags, ganchos) em toda geração.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-label">Nome</span>
              <input className="input-field mt-1" value={voice.name} onChange={(e) => setVoice({ ...voice, name: e.target.value })} />
            </label>
            {VOICE_FIELDS.map((f) => (
              <label key={f.key} className={clsx('block', f.rows > 2 && 'md:col-span-2')}>
                <span className="text-label">{f.label}</span>
                <textarea
                  className="textarea-field mt-1"
                  rows={f.rows}
                  value={(voice[f.key] as string | null) ?? ''}
                  onChange={(e) => setVoice({ ...voice, [f.key]: e.target.value })}
                />
                <span className="text-small text-slate-500">{f.help}</span>
              </label>
            ))}
          </div>
          <Button size="sm" icon={<Save />} loading={savingVoice} onClick={saveVoice}>
            Salvar tom de voz
          </Button>
        </section>
      )}

      {agents.map((a) => (
        <AgentCard key={a.agent_key} agent={a} onError={setError} onSaved={(s) => setAgents((list) => list.map((x) => (x.agent_key === s.agent_key ? s : x)))} />
      ))}
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
